import moment from "moment"
import _ from "lodash"
import {CalendarProps} from "react-calendar"
import {CustomCalendarProps, DateTimeShapeProps} from "./index"

// ******************   ****************** //
export const getFormatForShape = (shape: string): string => {
    if (shape === "hour_minute") {
        return 'HH:mm'
    } else if (shape === "month_day") {
        return 'dd-MM'
    } else if (shape === "year") {
        return 'y'
    } else if (shape === "year_month") {
        return 'MM-y'
    } else if (shape === "year_month_day") {
        return 'dd-MM-y'
    } else if (shape === "day_hour_minute") {
        return 'dd HH:mm'
    } else if (shape === "year_month_day_hour_minute") {
        return 'dd-MM-y HH:mm'
    } else {
        return ''
    }
}

// ******************   ****************** //
export const getDateForShapeFromValue = (shape: string, value: string): Date => {
    const passedValue = _.size(value) > 0 ? value : null
    const currentDay = new Date().getDay()
    const currentMonth = new Date().getMonth()
    const currentYear = new Date().getFullYear()

    let momentValue

    if (shape === "hour_minute") {
        momentValue = moment(passedValue ?? '00:00', getFormatForShape(shape)).year(1904).month(1).day(1)
    } else if (shape === "month_day") {
        momentValue = moment(passedValue ?? '01-01', getFormatForShape(shape).replaceAll('d', 'D')).year(1904)
    } else if (shape === "year") {
        momentValue = moment(passedValue ?? `${currentYear}`, getFormatForShape(shape).replaceAll('d', 'D')).month(1).day(1)
    } else if (shape === "year_month") {
        momentValue = moment(passedValue ?? `${currentMonth}-${currentYear}`, getFormatForShape(shape).replaceAll('d', 'D')).day(1)
    } else if (shape === "year_month_day") {
        momentValue = moment(passedValue ?? `${currentDay}-${currentMonth}-${currentYear}`, getFormatForShape(shape).replaceAll('d', 'D'))
    } else if (shape === "day_hour_minute") {
        momentValue = moment(passedValue ?? `${currentDay} 00:00`, getFormatForShape(shape).replaceAll('d', 'D')).year(1904).month(1)
    } else if (shape === "year_month_day_hour_minute") {
        momentValue = passedValue ? moment(passedValue, getFormatForShape(shape).replaceAll('d', 'D')) : moment()
    } else {
        momentValue = moment()
    }
    return momentValue.toDate()
}

// ******************   ****************** //
export const getStringDateForShapeFromDateValue = (shape: string, value: Date | string | null): string => {
    if (shape === 'hour_minute') {
        return <string>value
    } else {
        return moment(value).format(getFormatForShape(shape).replaceAll('d', 'D'))
    }
}

// ******************   ****************** //
export const getDateObjectForShapeFromObjectValue = (shape: string, valueObj: {
    year: string | null;
    month: string | null;
    day: string | null;
    hour: string | null;
    minute: string | null
}): Date | string | null => {

    let momentValue

    if (shape === "hour_minute") {
        // momentValue = moment(`${valueObj.hour}:${valueObj.minute}`, getFormatForShape(shape)).year(1904).month(1).day(1)
        if (!valueObj.hour || !valueObj.minute) {
            return null
        }
        momentValue = `${valueObj.hour}:${valueObj.minute}`
    } else if (shape === "month_day") {
        if (!valueObj.day || !valueObj.month) {
            return null
        }
        momentValue = moment(`${valueObj.day}-${valueObj.month}`, getFormatForShape(shape).replaceAll('d', 'D')).year(1904)
    } else if (shape === "year") {
        if (!valueObj.year) {
            return null
        }
        momentValue = moment(`${valueObj.year}`, getFormatForShape(shape).replaceAll('d', 'D')).month(1).day(1)
    } else if (shape === "year_month") {
        if (!valueObj.month || !valueObj.year) {
            return null
        }
        momentValue = moment(`${valueObj.month}-${valueObj.year}`, getFormatForShape(shape).replaceAll('d', 'D')).day(1)
    } else if (shape === "year_month_day") {
        if (!valueObj.day || !valueObj.month || !valueObj.year) {
            return null
        }
        momentValue = moment(`${valueObj.day}-${valueObj.month}-${valueObj.year}`, getFormatForShape(shape).replaceAll('d', 'D'))
    } else if (shape === "day_hour_minute") {
        if (!valueObj.day || !valueObj.hour || !valueObj.minute) {
            return null
        }
        momentValue = moment(`${valueObj.day} ${valueObj.hour}:${valueObj.minute}`, getFormatForShape(shape).replaceAll('d', 'D')).year(1904).month(1)
    } else if (shape === "year_month_day_hour_minute") {
        if (!valueObj.day || !valueObj.month || !valueObj.year || !valueObj.hour || !valueObj.minute) {
            return null
        }
        momentValue = moment(`${valueObj.day}-${valueObj.month}-${valueObj.year} ${valueObj.hour}:${valueObj.minute}`, getFormatForShape(shape).replaceAll('d', 'D'))
    } else {
        momentValue = moment()
    }
    return (typeof momentValue === 'string') ? momentValue : momentValue.toDate()
}

// ******************   ****************** //
export const getDateTimeParts = (format: string, value: string) => {
    const m = moment(value, format)
    if (m.isValid()) {
        return {
            // Year: if the format contains a 'Y' (like 'YY' or 'YYYY'), include the year
            year: /y/.test(format) ? `${m.year()}` : null,
            // Month: Moment's month() is zero-indexed, and token is uppercase M (e.g. 'MM')
            month: /MM/.test(format) ? `${m.format('MM') }` : null,
            // Day: Look for 'D' in the format (e.g. 'DD' or 'Do')
            day: /DD/.test(format) ? `${m.format('DD')}` : null,
            // Hour: Check for 24-hour ('H'/'HH') or 12-hour ('h'/'hh') tokens
            hour: /HH|hh/.test(format) ? `${m.format('HH')}` : null,
            // Minute: Lowercase m is used for minutes
            minute: /(^|[^A-Z])mm(?![A-Z])/.test(format) ? `${m.format('mm')}` : null
        }
    } else {

        return {
            year: null,
            month: null,
            day: null,
            hour: null,
            minute: null
        }
    }
}

// ******************   ****************** //
export const getStringValueByObjectValueAndShape = (value: { year: string | null; month: string | null; day: string | null; hour: string | null; minute: string | null }, shape: string) => {
    const date = getDateObjectForShapeFromObjectValue(shape, value)
    return getStringDateForShapeFromDateValue(shape, date)
}

// ******************   ****************** //
export const getDatetimePartsForResponseFromDatetimePickerResponse = (initParams: { datetimeShape: any; datetimePickerDetails: any }, value: any) => {
    const {datetimeShape, datetimePickerDetails} = initParams
    let val: string
    if (datetimeShape.type === 'time') {
        val = `${value}`
    } else {
        val = moment(`${value}`).format(datetimePickerDetails.format.replaceAll('d', 'D'))
    }

    return getDateTimeParts(datetimePickerDetails.format.replaceAll('d', 'D'), val)
}

// ******************   ****************** //
export const getClassesAndProps = (datetimeShape: DateTimeShapeProps): CustomCalendarProps => {
    let minDetail: CustomCalendarProps['minDetail']  = "decade"
    let maxDetail: CustomCalendarProps['maxDetail']  = "month"
    let minDate: CustomCalendarProps['minDate'] = undefined
    let maxDate: CustomCalendarProps['maxDate'] = undefined
    let hideDayNames: CustomCalendarProps['hideDayNames'] = false
    let hideMonth: CustomCalendarProps['hideMonth'] = false
    let formatMonthYear: CustomCalendarProps['formatMonthYear'] = undefined
    let formatYear: CustomCalendarProps['formatYear'] = undefined
    let defaultActiveStartDate: CustomCalendarProps['defaultActiveStartDate'] = undefined
    // let format: CustomCalendarProps['format'] = 'dd-MM-y HH:mm'
    const format: CustomCalendarProps['format'] = getFormatForShape(datetimeShape.value)

    if (datetimeShape.value === "month_day") {
        minDetail = 'year'
        maxDetail = 'month'
        // format = 'dd-MM'

        // Set minDate to the first day of the current year
        minDate = new Date(4, 0, 1) // January 1st
        maxDate = new Date(4, 11, 31) // December 31st
        defaultActiveStartDate = new Date(4, 0, 1)

        hideDayNames = true
        hideMonth = false
        formatMonthYear = (locale, date) => date.toLocaleString(locale, { month: 'long' })
        formatYear = () => ''
    } else if (datetimeShape.value === "year") {
        minDetail = 'century'
        maxDetail = 'decade'
        // format = 'y'
    } else if (datetimeShape.value === "year_month") {
        minDetail = 'century'
        maxDetail = 'year'
        // format = 'MM-y'
    } else if (datetimeShape.value === "year_month_day") {
        minDetail = 'century'
        maxDetail = 'month'
        // format = 'dd-MM-y'
    } else if (datetimeShape.value === "day_hour_minute") {
        minDetail = 'month'
        maxDetail = 'month'
        // format = 'dd HH:mm'

        // Set minDate to the first day of the current year
        minDate = new Date(4, 0, 1) // January 1st
        maxDate = new Date(4, 0, 31) // January 31st
        defaultActiveStartDate = new Date(4, 0, 1)

        hideDayNames = true
        hideMonth = true
        formatMonthYear = (locale, date) => date.toLocaleString(locale, { month: 'long' })
    } else if (datetimeShape.value === "year_month_day_hour_minute") {
        minDetail = 'century'
        maxDetail = 'month'
        // format = 'dd-MM-y HH:mm'
    }

    return {
        hideDayNames,
        hideMonth,
        formatMonthYear,
        formatYear,
        minDetail,
        maxDetail,
        minDate,
        maxDate,
        defaultActiveStartDate,
        format
    }
}
